import React from 'react';

const AutomationSection: React.FC = () => {
    return (
        <section className="py-20 bg-light">
            <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-16">
                    <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">
                        AI Automation
                    </h2>
                    <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                        Streamline your business processes with intelligent automation solutions
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {/* Invoice Automation */}
                    <div className="group relative bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                        <div className="relative z-10">
                            <div className="w-16 h-16 bg-primary rounded-2xl transition-transform duration-300 flex items-center justify-center mb-6 shadow-lg shadow-primary/20 group-hover:scale-105">
                                <svg className="w-8 h-8 text-white transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 group-hover:text-primary transition-colors duration-300 mb-4">Invoice Automation</h3>

                            <div className="mb-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Problem</h4>
                                <p className="text-gray-600">Manual invoice processing, errors, and delayed payments</p>
                            </div>

                            <div className="mb-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Solution</h4>
                                <p className="text-gray-600">AI-powered invoice extraction, validation, and payment processing</p>
                            </div>

                            <div className="mb-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Benefits</h4>
                                <ul className="text-gray-600 space-y-1">
                                    <li>• 80% faster processing</li>
                                    <li>• Reduced errors</li>
                                    <li>• Improved cash flow</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Lead Follow-up Automation */}
                    <div className="group relative bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-br from-secondary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                        <div className="relative z-10">
                            <div className="w-16 h-16 bg-secondary rounded-2xl transition-transform duration-300 flex items-center justify-center mb-6 shadow-lg shadow-secondary/20 group-hover:scale-105">
                                <svg className="w-8 h-8 text-white transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                </svg>
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 group-hover:text-secondary transition-colors duration-300 mb-4">Lead Follow-up Automation</h3>

                            <div className="mb-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Problem</h4>
                                <p className="text-gray-600">Lost leads due to delayed follow-up and inconsistent communication</p>
                            </div>

                            <div className="mb-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Solution</h4>
                                <p className="text-gray-600">Automated lead nurturing with personalized sequences and scoring</p>
                            </div>

                            <div className="mb-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Benefits</h4>
                                <ul className="text-gray-600 space-y-1">
                                    <li>• 50% more conversions</li>
                                    <li>• Consistent follow-up</li>
                                    <li>• Better lead qualification</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* HR Onboarding Automation */}
                    <div className="group relative bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-br from-dark/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                        <div className="relative z-10">
                            <div className="w-16 h-16 bg-dark rounded-2xl transition-transform duration-300 flex items-center justify-center mb-6 shadow-lg shadow-dark/20 group-hover:scale-105">
                                <svg className="w-8 h-8 text-white transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                                </svg>
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 group-hover:text-dark transition-colors duration-300 mb-4">HR Onboarding Automation</h3>

                            <div className="mb-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Problem</h4>
                                <p className="text-gray-600">Time-consuming onboarding process and paperwork management</p>
                            </div>

                            <div className="mb-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Solution</h4>
                                <p className="text-gray-600">Automated onboarding workflows, document collection, and training schedules</p>
                            </div>

                            <div className="mb-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Benefits</h4>
                                <ul className="text-gray-600 space-y-1">
                                    <li>• 70% faster onboarding</li>
                                    <li>• Better employee experience</li>
                                    <li>• Compliance tracking</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Email Writing Automation */}
                    <div className="group relative bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                        <div className="relative z-10">
                            <div className="w-16 h-16 bg-primary rounded-2xl transition-transform duration-300 flex items-center justify-center mb-6 shadow-lg shadow-primary/20 group-hover:scale-105">
                                <svg className="w-8 h-8 text-white transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 group-hover:text-primary transition-colors duration-300 mb-4">Email Writing Automation</h3>

                            <div className="mb-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Problem</h4>
                                <p className="text-gray-600">Time-consuming email composition and inconsistent messaging</p>
                            </div>

                            <div className="mb-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Solution</h4>
                                <p className="text-gray-600">AI-powered email generation with personalization and brand consistency</p>
                            </div>

                            <div className="mb-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Benefits</h4>
                                <ul className="text-gray-600 space-y-1">
                                    <li>• Save 10+ hours weekly</li>
                                    <li>• Higher open rates</li>
                                    <li>• Consistent branding</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Social Media Automation */}
                    <div className="group relative bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-br from-secondary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                        <div className="relative z-10">
                            <div className="w-16 h-16 bg-secondary rounded-2xl transition-transform duration-300 flex items-center justify-center mb-6 shadow-lg shadow-secondary/20 group-hover:scale-105">
                                <svg className="w-8 h-8 text-white transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4v16M17 4v16M3 8h4m10 0h4M3 16h4m10 0h4" />
                                </svg>
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 group-hover:text-secondary transition-colors duration-300 mb-4">Social Media Automation</h3>

                            <div className="mb-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Problem</h4>
                                <p className="text-gray-600">Manual posting, inconsistent content, and engagement management</p>
                            </div>

                            <div className="mb-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Solution</h4>
                                <p className="text-gray-600">Automated content scheduling, posting, and engagement analytics</p>
                            </div>

                            <div className="mb-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Benefits</h4>
                                <ul className="text-gray-600 space-y-1">
                                    <li>• Consistent posting schedule</li>
                                    <li>• Better engagement</li>
                                    <li>• Time savings</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default AutomationSection;
